import { FormEvent, useEffect, useRef, useState } from 'react'
import { IconArrowRight, IconMail } from '@tabler/icons-react'

import appStoreBadge from '../assets/images/asterode-download-app-store.png'
import googlePlayBadge from '../assets/images/asterode-download-google-play.png'
import favicon from '../assets/favicon.png'

import '../styles/home-rebuild.css'

const APP_STORE_URL = 'https://apps.apple.com/us/app/asterode/id6758882535'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ai.asterode.app'

const Home02 = () => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [email, setEmail] = useState('')
  const [subState, setSubState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [subMsg, setSubMsg] = useState('')

  useEffect(() => {
    if (!rootRef.current) return
    const els = Array.from(rootRef.current.querySelectorAll<HTMLElement>('.v5-reveal'))
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target) } }),
      { threshold: 0.12 },
    )
    els.forEach(el => obs.observe(el))
    return () => obs.disconnect()
  }, [])

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setSubState('error'); setSubMsg('Please enter a valid email address.'); return
    }
    setSubState('submitting'); setSubMsg('Signing you up...')
    try {
      const res = await fetch('/.netlify/functions/subscribe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      })
      const data = (await res.json().catch(() => null)) as { message?: string; error?: string } | null
      if (!res.ok) { setSubState('error'); setSubMsg(data?.error ?? 'Could not subscribe. Try again.'); return }
      setEmail(''); setSubState('success'); setSubMsg(data?.message ?? 'Check your inbox to confirm.')
    } catch { setSubState('error'); setSubMsg('Could not subscribe. Try again.') }
  }

  return (
    <div ref={rootRef} className="v5-home">
      <section id="updates" className="v5-cta-section v5-cta-section--fullscreen">
        <div className="container-shell v5-cta-section--fullscreen-inner">
          <img src={favicon} alt="Asterode" className="v5-cta-favicon v5-reveal" width={48} height={48} />
          <div className="v5-reveal">
            <h2>Stop dating different AI models every week.</h2>
            <p className="v5-cta-sub">
                Date every model at once with zero drama. One app with the best AIs working together, plus notes and memory to keep your stories straight.
            </p>
            <div className="v5-cta-stores">
              <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
                <img src={appStoreBadge} alt="Download on the App Store" />
              </a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
                <img src={googlePlayBadge} alt="Get it on Google Play" />
              </a>
            </div>
          </div>

          <div className="v5-cta-divider" />

          <div className="v5-reveal v5-reveal-delay-2">
            <p className="v5-cta-email-label">Get product updates</p>
            <form onSubmit={handleSubscribe} className="v5-subscribe-form">
              <input
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                disabled={subState === 'submitting'}
              />
              <button type="submit" disabled={subState === 'submitting'} className="flex items-center justify-center gap-2">
                {subState === 'submitting' ? (
                  'Subscribing...'
                ) : (
                  <>
                    <IconMail size={18} stroke={1.8} />
                    <span>Subscribe</span>
                    <IconArrowRight size={18} stroke={1.8} />
                  </>
                )}
              </button>
            </form>
            {subMsg && <p className={`v5-subscribe-msg ${subState}`}>{subMsg}</p>}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home02
